import { useRef, useState } from "react";
import { Camera, Loader2 } from "lucide-react";
import { API, api, apiError, uploadFile } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";

/** Loading and delivery photos for a challan, filed in the Dispatch Proof vault folder. */
export function DispatchProofDialog({ challan, onClose, onSaved }) {
  const [photos, setPhotos] = useState(challan.proof_photos || []);
  const [busy, setBusy] = useState(false);
  const [kind, setKind] = useState("loading");
  const camera = useRef(null);

  const upload = async (files) => {
    setBusy(true);
    try {
      const ids = [];
      for (const f of Array.from(files)) {
        const up = await uploadFile(f, "dispatch_proof", challan.id);
        ids.push(up.id);
      }
      const { data } = await api.post(`/challans/${challan.id}/proof`,
        { file_ids: ids, kind });
      setPhotos([...photos, ...data.photos]);
      toast.success(`${data.added} photo(s) saved to the Dispatch Proof folder`);
      onSaved?.();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg" data-testid="dispatch-proof-dialog">
        <DialogHeader>
          <DialogTitle>Dispatch proof — {challan.number}</DialogTitle>
          <DialogDescription>
            Photograph the loaded vehicle before it leaves, and the delivered goods after
            handover. Photos stay in the Dispatch Proof vault folder against this challan.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap gap-2">
          {["loading", "delivered"].map((k) => (
            <Button key={k} size="sm" variant={kind === k ? "default" : "outline"}
              data-testid={`proof-kind-${k}`} onClick={() => setKind(k)}>
              {k === "loading" ? "Loading photo" : "Delivered photo"}
            </Button>
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <input ref={camera} type="file" accept="image/*" capture="environment" multiple hidden
            data-testid="proof-camera-input"
            onChange={(e) => e.target.files?.length && upload(e.target.files)} />
          <Button variant="outline" className="gap-1.5" disabled={busy}
            data-testid="proof-camera-button" onClick={() => camera.current?.click()}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" />
              : <Camera className="h-4 w-4" />}
            Take photo
          </Button>
          <input type="file" accept="image/*,application/pdf" multiple className="text-xs"
            data-testid="proof-file-input"
            onChange={(e) => e.target.files?.length && upload(e.target.files)} />
        </div>

        {photos.length > 0 && (
          <div className="grid grid-cols-3 gap-2" data-testid="proof-photo-list">
            {photos.map((p) => (
              <a key={p.file_id} href={`${API}/files/${p.file_id}`} target="_blank"
                rel="noreferrer" className="rounded border border-border p-1 text-[10px]"
                data-testid={`proof-photo-${p.file_id}`}>
                <img src={`${API}/files/${p.file_id}`} alt={p.filename}
                  className="h-20 w-full rounded object-contain" />
                <Badge variant="secondary" className="mt-1 text-[9px]">{p.kind}</Badge>
              </a>
            ))}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="proof-done">Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
